import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="demo-upload"
export default class extends Controller {
  static targets = ["uploadPanel", "samplePanel", "fileInput", "submitButton", "previewContainer", "hiddenInputsContainer"]

  connect() {
    this.selectedFiles = []
    this.setupTabListeners()
    this.setupDragAndDrop()
    this.updateSubmitButtonState()
    this.createHiddenInputsContainer()
  }

  createHiddenInputsContainer() {
    if (!this.hasHiddenInputsContainerTarget) {
      const container = document.createElement('div')
      container.dataset.demoUploadTarget = 'hiddenInputsContainer'
      container.style.display = 'none'
      this.element.appendChild(container)
    }
  }

  setupTabListeners() {
    const uploadTab = this.element.querySelector('input[name="upload_type"]#upload_tab')
    const sampleTab = this.element.querySelector('input[name="upload_type"]#sample_tab')

    if (uploadTab) {
      uploadTab.addEventListener('change', () => {
        if (uploadTab.checked) {
          this.showUploadPanel()
        }
      })
    }

    if (sampleTab) {
      sampleTab.addEventListener('change', () => {
        if (sampleTab.checked) {
          this.showSamplePanel()
        }
      })
    }

    // Setup sample selection
    this.setupSampleSelection()
  }

  setupSampleSelection() {
    const sampleOptions = this.element.querySelectorAll('.sample-option')
    sampleOptions.forEach(option => {
      option.addEventListener('click', () => {
        // Clear previous selections
        sampleOptions.forEach(opt => {
          opt.classList.remove('border-primary-500')
          opt.classList.add('border-transparent')
          const radio = opt.querySelector('input[type="radio"]')
          if (radio) radio.checked = false
        })

        // Select this option
        option.classList.remove('border-transparent')
        option.classList.add('border-primary-500')
        const radio = option.querySelector('input[type="radio"]')
        if (radio) {
          radio.checked = true
          this.updateSubmitButtonState()
        }
      })
    })
  }

  showUploadPanel() {
    if (this.hasUploadPanelTarget) {
      this.uploadPanelTarget.classList.remove('hidden')
    }
    if (this.hasSamplePanelTarget) {
      this.samplePanelTarget.classList.add('hidden')
    }
    this.updateSubmitButtonState()
  }

  showSamplePanel() {
    if (this.hasUploadPanelTarget) {
      this.uploadPanelTarget.classList.add('hidden')
    }
    if (this.hasSamplePanelTarget) {
      this.samplePanelTarget.classList.remove('hidden')
    }
    this.updateSubmitButtonState()
  }

  triggerUpload() {
    if (this.hasFileInputTarget) {
      this.fileInputTarget.click()
    }
  }

  handleFileSelect(event) {
    const files = Array.from(event.target.files)
    if (files.length > 0) {
      const validation = this.validateFiles(files)
      if (validation.isValid) {
        this.clearError()
        this.addFiles(files)
      } else {
        this.showError(validation.error)
      }
      this.updateSubmitButtonState()
    }
    // Clear the input to prevent duplicate submission (we use hidden inputs instead)
    event.target.value = ''
  }

  addFiles(files) {
    files.forEach(file => {
      const fileId = `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      this.selectedFiles.push({ id: fileId, file: file })
      this.addFilePreview(file, fileId)
      this.createHiddenFileInput(file, fileId)
    })
  }

  createHiddenFileInput(file, fileId) {
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)

    const hiddenInput = document.createElement('input')
    hiddenInput.type = 'file'
    hiddenInput.name = 'demo_upload[images][]'
    hiddenInput.files = dataTransfer.files
    hiddenInput.dataset.fileId = fileId
    hiddenInput.style.display = 'none'

    this.hiddenInputsContainerTarget.appendChild(hiddenInput)
  }

  addFilePreview(file, fileId) {
    const previewDiv = document.createElement('div')
    previewDiv.className = 'relative inline-block'
    previewDiv.dataset.fileId = fileId

    const img = document.createElement('img')
    img.className = 'w-32 h-32 object-cover rounded-lg'
    img.src = URL.createObjectURL(file)

    const removeBtn = document.createElement('button')
    removeBtn.type = 'button'
    removeBtn.className = 'absolute -top-2 -right-2 btn btn-circle btn-sm btn-error'
    removeBtn.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
      </svg>
    `
    removeBtn.addEventListener('click', () => this.removeFile(fileId))

    previewDiv.appendChild(img)
    previewDiv.appendChild(removeBtn)

    if (this.hasPreviewContainerTarget) {
      this.previewContainerTarget.appendChild(previewDiv)
      this.previewContainerTarget.classList.remove('hidden')
    }
  }

  removeFile(fileId) {
    // Remove from selectedFiles array
    const index = this.selectedFiles.findIndex(f => f.id === fileId)
    if (index > -1) {
      this.selectedFiles.splice(index, 1)
    }

    // Remove preview
    const previewDiv = this.previewContainerTarget.querySelector(`[data-file-id="${fileId}"]`)
    if (previewDiv) {
      previewDiv.remove()
    }

    // Remove hidden input
    const hiddenInput = this.hiddenInputsContainerTarget.querySelector(`input[data-file-id="${fileId}"]`)
    if (hiddenInput) {
      hiddenInput.remove()
    }

    if (this.selectedFiles.length === 0 && this.hasPreviewContainerTarget) {
      this.previewContainerTarget.classList.add('hidden')
    }

    this.updateSubmitButtonState()
  }

  validateFiles(files) {
    const MAX_FILES = 5
    const MAX_SIZE_MB = 10
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024

    const totalFiles = this.selectedFiles.length + files.length
    if (totalFiles > MAX_FILES) {
      return {
        isValid: false,
        error: `You can only upload a maximum of ${MAX_FILES} photos. You currently have ${this.selectedFiles.length} photo(s) selected.`
      }
    }

    for (let file of files) {
      if (file.size > MAX_SIZE_BYTES) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1)
        return {
          isValid: false,
          error: `File "${file.name}" is ${sizeMB}MB, which exceeds the ${MAX_SIZE_MB}MB limit.`
        }
      }
    }

    return { isValid: true }
  }

  showError(message) {
    const uploadPanel = this.uploadPanelTarget

    this.clearError()

    const errorDiv = document.createElement('div')
    errorDiv.className = 'error-message alert alert-error mt-4'
    errorDiv.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span>${message}</span>
    `
    uploadPanel.appendChild(errorDiv)
  }

  clearError() {
    const existingError = this.uploadPanelTarget.querySelector('.error-message')
    if (existingError) {
      existingError.remove()
    }
  }

  updateSubmitButtonState() {
    if (!this.hasSubmitButtonTarget) return

    const uploadTabChecked = this.element.querySelector('input[name="upload_type"]#upload_tab')?.checked
    const sampleTabChecked = this.element.querySelector('input[name="upload_type"]#sample_tab')?.checked

    let canSubmit = false

    if (uploadTabChecked) {
      canSubmit = this.selectedFiles.length > 0
    } else if (sampleTabChecked) {
      const selectedSample = this.element.querySelector('input[name="demo_upload[public_subject_uuid]"]:checked')
      canSubmit = !!selectedSample
    }

    this.submitButtonTarget.disabled = !canSubmit

    if (canSubmit) {
      this.submitButtonTarget.classList.remove('btn-disabled')
    } else {
      this.submitButtonTarget.classList.add('btn-disabled')
    }
  }

  // Handle drag and drop
  setupDragAndDrop() {
    if (!this.hasUploadPanelTarget) return

    const uploadPanel = this.uploadPanelTarget

    uploadPanel.addEventListener('dragover', (e) => {
      e.preventDefault()
      uploadPanel.classList.add('border-primary-500', 'bg-primary-50')
    })

    uploadPanel.addEventListener('dragleave', (e) => {
      e.preventDefault()
      uploadPanel.classList.remove('border-primary-500', 'bg-primary-50')
    })

    uploadPanel.addEventListener('drop', (e) => {
      e.preventDefault()
      uploadPanel.classList.remove('border-primary-500', 'bg-primary-50')

      const files = Array.from(e.dataTransfer.files)
      if (files.length > 0) {
        const validation = this.validateFiles(files)
        if (validation.isValid) {
          this.clearError()
          this.addFiles(files)
        } else {
          this.showError(validation.error)
        }
        this.updateSubmitButtonState()
      }
    })
  }
}